import { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { Menu, X, CheckCircle2, MessageCircle } from 'lucide-react';
import { useAuthStore } from '@/lib/auth-store';
import { ProAdvLogo } from '@/components/brand/proadv-logo';
import { LandingHero } from './components/hero';
import { LandingDiferenciais } from './components/diferenciais';
import { LandingModuleShowcase } from './components/module-showcase';
import { LandingPlansSection } from './components/plans-section';
import { LandingLeadForm } from './components/lead-form';
import { WhatsAppFab, buildWhatsAppLink } from './components/whatsapp-fab';

export default function LandingPage() {
  const status = useAuthStore((s) => s.status);
  const tenantId = useAuthStore((s) => s.tenantId);

  // Redireciona se já autenticado com tenant
  if (status === 'authenticated' && tenantId) {
    return <Navigate to="/escritorio" replace />;
  }

  // Enquanto bootstrap ainda carrega, renderiza a landing sem piscar.
  // (status === 'unknown' | 'authenticating') → mostra landing normalmente.
  // Quando autenticado sem tenant → também mostra landing (ou poderia
  // redirecionar p/ /sem-escritorio, mas aqui mantemos landing pública).

  return (
    <div className="landing-root">
      <GrainOverlay />
      <MeshBackground />
      <LandingHeader />
      <LandingHero />
      <LandingDiferenciais />
      <LandingModuleShowcase />
      <LandingLeadFormSection />
      <LandingPlansSection />
      <LandingFooter />
      <WhatsAppFab />
    </div>
  );
}

function GrainOverlay() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.035,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
        backgroundSize: '300px 300px',
      }}
    />
  );
}

function MeshBackground() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        background: '#0A0710',
      }}
    >
      {/* Glow top-left */}
      <div
        style={{
          position: 'absolute',
          top: '-10%',
          left: '-5%',
          width: '60vw',
          height: '60vw',
          maxWidth: 900,
          maxHeight: 900,
          background:
            'radial-gradient(ellipse at center, rgba(109,40,217,0.28) 0%, rgba(109,40,217,0.08) 45%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(1px)',
        }}
      />
      {/* Glow bottom-right */}
      <div
        style={{
          position: 'absolute',
          bottom: '-10%',
          right: '-5%',
          width: '55vw',
          height: '55vw',
          maxWidth: 800,
          maxHeight: 800,
          background:
            'radial-gradient(ellipse at center, rgba(168,85,247,0.22) 0%, rgba(168,85,247,0.06) 45%, transparent 70%)',
          borderRadius: '50%',
          filter: 'blur(1px)',
        }}
      />
      {/* Glow mid-center sutil */}
      <div
        style={{
          position: 'absolute',
          top: '35%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '70vw',
          height: '40vw',
          maxWidth: 1000,
          maxHeight: 600,
          background:
            'radial-gradient(ellipse at center, rgba(109,40,217,0.10) 0%, transparent 65%)',
          borderRadius: '50%',
        }}
      />
    </div>
  );
}

const NAV_LINKS = [
  { href: '#dashboard', label: 'Dashboard' },
  { href: '#processos', label: 'Processos' },
  { href: '#intimacoes', label: 'Intimações' },
  { href: '#atividades', label: 'Atividades' },
  { href: '#agenda', label: 'Agenda' },
  { href: '#documentos', label: 'Documentos' },
  { href: '#financeiro', label: 'Financeiro' },
  { href: '#relatorios', label: 'Relatórios' },
  { href: '#planos', label: 'Planos' },
];

function LandingHeader() {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setMenuOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <style>{HEADER_STYLES}</style>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          background: 'rgba(10,7,16,0.88)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 clamp(20px, 4vw, 48px)',
          height: 56,
          gap: 16,
        }}
      >
        {/* Marca */}
        <ProAdvLogo variant="horizontal" tone="dark" size={30} />

        {/* Links desktop */}
        <nav className="landing-nav-desktop">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleAnchorClick(e, link.href)}
              className="landing-nav-link"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Lado direito: Entrar + hamburguer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <Link
            to="/sign-in"
            style={{
              fontFamily: "'Hanken Grotesk', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              color: '#F5F1FA',
              textDecoration: 'none',
              padding: '7px 18px',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.16)',
              background: 'rgba(255,255,255,0.04)',
              whiteSpace: 'nowrap',
            }}
          >
            Entrar
          </Link>
          <button
            className="landing-hamburger"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menu"
            style={{
              background: 'none',
              border: 'none',
              padding: 4,
              cursor: 'pointer',
              color: '#A99FB8',
              display: 'none',
            }}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </header>

      {/* Dropdown mobile */}
      {menuOpen && (
        <div
          className="landing-mobile-menu"
          style={{
            position: 'fixed',
            top: 56,
            left: 0,
            right: 0,
            zIndex: 99,
            background: 'rgba(10,7,16,0.97)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            padding: '16px 24px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleAnchorClick(e, link.href)}
              style={{
                fontFamily: "'Hanken Grotesk', sans-serif",
                fontSize: 15,
                fontWeight: 500,
                color: '#A99FB8',
                textDecoration: 'none',
                padding: '10px 0',
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                transition: 'color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = '#F0C674';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.color = '#A99FB8';
              }}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </>
  );
}

const HEADER_STYLES = `
  .landing-nav-desktop {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 1;
    justify-content: center;
  }

  .landing-nav-link {
    font-family: 'Hanken Grotesk', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #A99FB8;
    text-decoration: none;
    padding: 5px 10px;
    border-radius: 6px;
    transition: color 0.15s ease, background 0.15s ease;
    white-space: nowrap;
  }

  .landing-nav-link:hover {
    color: #F0C674;
    background: rgba(232,185,106,0.06);
  }

  @media (max-width: 1023px) {
    .landing-nav-desktop {
      display: none;
    }
    .landing-hamburger {
      display: block !important;
    }
  }

  /* Regra global anti scroll-horizontal na pagina */
  .landing-root {
    overflow-x: hidden;
    max-width: 100vw;
  }
`;

const LEAD_CHAMPAGNE = '#E8B96A';
const LEAD_FG = '#F5F1FA';
const LEAD_MUTED = '#A99FB8';

const LEAD_BULLETS = [
  'Veja o VoxisAdv rodando com processos reais',
  'Tire dúvidas sobre planos, preços e implantação',
  'Sem compromisso, conversa direta com quem constrói o produto',
];

function LandingLeadFormSection() {
  return (
    <section
      id="acesso"
      style={{
        position: 'relative',
        zIndex: 1,
        padding: '80px 24px 100px',
      }}
    >
      <style>{LEAD_SECTION_STYLES}</style>
      <div className="landing-lead-grid">
        <LeadPitchColumn />
        <LandingLeadForm />
      </div>
    </section>
  );
}

function LeadPitchColumn() {
  return (
    <div>
      <span
        style={{
          fontFamily: "'Hanken Grotesk', sans-serif",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.12em',
          color: LEAD_CHAMPAGNE,
          textTransform: 'uppercase',
        }}
      >
        Agende uma demonstração
      </span>
      <h2
        style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontWeight: 400,
          fontSize: 'clamp(30px, 4.5vw, 44px)',
          letterSpacing: '-0.01em',
          color: LEAD_FG,
          margin: '14px 0 18px',
          lineHeight: 1.15,
        }}
      >
        Agende uma demonstração e tire suas dúvidas
      </h2>
      <p
        style={{
          fontFamily: "'Hanken Grotesk', sans-serif",
          fontSize: 16,
          color: LEAD_MUTED,
          lineHeight: 1.6,
          marginBottom: 32,
          maxWidth: 440,
        }}
      >
        Marque uma conversa rápida com nossa equipe para ver o VoxisAdv
        funcionando em um escritório de verdade e esclarecer qualquer dúvida.
      </p>

      <ul
        style={{
          listStyle: 'none',
          margin: '0 0 36px',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {LEAD_BULLETS.map((bullet) => (
          <li
            key={bullet}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}
          >
            <span style={{ flexShrink: 0, marginTop: 1, color: LEAD_CHAMPAGNE }}>
              <CheckCircle2 size={20} />
            </span>
            <span
              style={{
                fontFamily: "'Hanken Grotesk', sans-serif",
                fontSize: 15,
                color: LEAD_FG,
                lineHeight: 1.5,
              }}
            >
              {bullet}
            </span>
          </li>
        ))}
      </ul>

      <div>
        <p
          style={{
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontSize: 13,
            color: LEAD_MUTED,
            marginBottom: 10,
          }}
        >
          Prefere falar direto?
        </p>
        <a
          href={buildWhatsAppLink()}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#25D366',
            color: '#FFFFFF',
            fontFamily: "'Hanken Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: 14,
            padding: '12px 22px',
            borderRadius: 10,
            textDecoration: 'none',
            boxShadow: '0 4px 20px rgba(37,211,102,0.28)',
          }}
        >
          <MessageCircle size={18} />
          Falar no WhatsApp
        </a>
      </div>
    </div>
  );
}

const LEAD_SECTION_STYLES = `
  .landing-lead-grid {
    max-width: 1080px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: clamp(32px, 6vw, 64px);
    align-items: center;
  }

  @media (max-width: 900px) {
    .landing-lead-grid {
      grid-template-columns: 1fr;
      gap: 40px;
    }
  }
`;

function LandingFooter() {
  return (
    <footer
      style={{
        position: 'relative',
        zIndex: 1,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: '32px 24px',
        textAlign: 'center',
        color: 'rgba(169,159,184,0.7)',
        fontFamily: "'Hanken Grotesk', sans-serif",
        fontSize: 13,
        letterSpacing: '0.02em',
      }}
    >
      VoxisAdv &middot; CRM Jurídico &copy; {new Date().getFullYear()}. Todos os direitos reservados.
    </footer>
  );
}
